import { clearAnnualBehaviorNotesCache } from '../services/annualBehaviorNotesService';
import type { ProgramScopeType } from '../types/domain';
import { clearAnnualProgressDataCache } from './annualProgressDataCache';
import { clearAnnualProgressScopedTermCache } from './annualProgressScopedTermCache';
import { clearProgramFixedContentRowsCache } from './programFixedContentRowsCache';
import { clearProgramProgressCellsCache } from './programProgressCellsCache';
import { clearProgramProgressVolatileInCache } from './programProgressDataCache';
import { clearProgramScopedContentRowsCache } from './programScopedContentRowsCache';
import { deviceCacheStorage } from './deviceCacheStorage';

/**
 * 연간시간표 저장 등으로 연간진도표에 쓰는 연간·학기 단위 로컬 캐시를 비웁니다.
 */
export function clearAnnualProgressTermAggregateCaches(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2
): void {
  clearAnnualProgressDataCache(workspaceId, academicYear, termNumber);
}

/** 프로그램·수업기록 표시용 캐시(행동특성 제외) */
function clearAllProgramScopedContentRowsCachesForGrade(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeId: string
): void {
  if (!workspaceId || !gradeId || typeof window === 'undefined') return;
  const prefix = `cp.programScopedContentRows.`;
  const segment = `.${workspaceId}.${academicYear}.${termNumber}.${gradeId}.`;
  const keysToRemove: string[] = [];
  for (let index = 0; index < deviceCacheStorage.length; index += 1) {
    const key = deviceCacheStorage.key(index);
    if (key && key.startsWith(prefix) && key.includes(segment)) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach((key) => deviceCacheStorage.removeItem(key));
}

export function clearProgramDisplayCachesForGrade(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeId: string
): void {
  if (!gradeId) return;
  clearProgramFixedContentRowsCache(workspaceId, academicYear, termNumber, gradeId);
  clearProgramProgressCellsCache(workspaceId, academicYear, termNumber, gradeId);
  clearAnnualProgressScopedTermCache(workspaceId, academicYear, termNumber, gradeId);
  clearAllProgramScopedContentRowsCachesForGrade(workspaceId, academicYear, termNumber, gradeId);
  clearProgramProgressVolatileInCache(workspaceId, academicYear, termNumber, gradeId);
}

/** 대시보드 수업기록 저장 후 — 해당 고정·스코프 행 캐시만 비움 */
export function clearProgramLessonRecordCaches(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeId: string,
  source: { type: 'fixed' } | { type: 'scoped'; scopeType: ProgramScopeType; scopeKey: string }
): void {
  if (!gradeId) return;
  if (source.type === 'fixed') {
    clearProgramFixedContentRowsCache(workspaceId, academicYear, termNumber, gradeId);
    return;
  }
  const scopeKey = source.scopeKey.trim();
  if (!scopeKey) return;
  clearProgramScopedContentRowsCache(
    workspaceId,
    academicYear,
    termNumber,
    gradeId,
    source.scopeType,
    scopeKey
  );
  clearAnnualProgressScopedTermCache(workspaceId, academicYear, termNumber, gradeId);
}

export function clearBehaviorNotesCachesForGrade(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeId: string
): void {
  if (!gradeId) return;
  clearAnnualBehaviorNotesCache(workspaceId, academicYear, termNumber, gradeId);
}

/**
 * 프로그램 진도표 저장 또는 연간시간표 저장 연동으로, 연간진도표가 참고하는 학년·학기별 로컬 캐시를 비웁니다.
 */
export function clearAnnualProgressRelatedCachesForGrade(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeId: string
): void {
  if (!gradeId) return;
  clearProgramDisplayCachesForGrade(workspaceId, academicYear, termNumber, gradeId);
  clearBehaviorNotesCachesForGrade(workspaceId, academicYear, termNumber, gradeId);
}
